import { EDITOR_MODEL_CHANGED_EVENT } from "./editorProjectDirty";
import { getProjectSaveUiState, subscribeProjectSaveUiState } from "./projectSaveUiRuntime";
import {
  clearEditorUndoHistoryForScreenTransition,
  type EditorUndoHistoryScreenTransitionReason,
} from "./editorUndoHistoryRuntime";
import {
  SPACE_NAV_TRANSACTION_STARTED_EVENT,
  subscribeSpaceNavigationEvent,
} from "./spaceNavigationEvents";

export const EDITOR_UNDO_TOAST_INVALIDATED_EVENT = "editor:undoToastInvalidated";

export const invalidateEditorUndoToast = (): void => {
  window.dispatchEvent(new Event(EDITOR_UNDO_TOAST_INVALIDATED_EVENT));
};

export function dismissEditorUndoToastForScreenTransition(
  reason: EditorUndoHistoryScreenTransitionReason,
): void {
  invalidateEditorUndoToast();
  void clearEditorUndoHistoryForScreenTransition(reason);
}

let screenTransitionListenerCleanup: (() => void) | null = null;

export function beginEditorUndoToastScreenTransitionSession(): void {
  endEditorUndoToastScreenTransitionSession();
  if (typeof window === "undefined") return;

  screenTransitionListenerCleanup = subscribeSpaceNavigationEvent(
    SPACE_NAV_TRANSACTION_STARTED_EVENT,
    () => {
      dismissEditorUndoToastForScreenTransition("space_changed");
    },
  );
}

export function endEditorUndoToastScreenTransitionSession(): void {
  screenTransitionListenerCleanup?.();
  screenTransitionListenerCleanup = null;
}

/** UI-only boundary: never consumes a Unity token or clears Undo history. */
export function subscribeEditorUndoToastInvalidation(
  invalidate: () => void,
  ownsChange: (changeType: string) => boolean = () => false,
): () => void {
  const changed = (event: Event) => {
    const detail = (event as CustomEvent<{ isDirty?: boolean; changeType?: string }>).detail;
    if (detail?.isDirty === true && !ownsChange(detail.changeType ?? "")) invalidate();
  };
  const events = [EDITOR_UNDO_TOAST_INVALIDATED_EVENT, "editor:prototypeMutationStarted"];
  events.forEach(name => window.addEventListener(name, invalidate));
  window.addEventListener(EDITOR_MODEL_CHANGED_EVENT, changed);
  let saveAttemptId = getProjectSaveUiState().activeOperation?.saveAttemptId;
  const unsubscribeSave = subscribeProjectSaveUiState(() => {
    const state = getProjectSaveUiState();
    const nextId = state.activeOperation?.saveAttemptId;
    if (state.status === "saving" && nextId && nextId !== saveAttemptId) invalidate();
    saveAttemptId = nextId;
  });
  return () => {
    events.forEach(name => window.removeEventListener(name, invalidate));
    window.removeEventListener(EDITOR_MODEL_CHANGED_EVENT, changed);
    unsubscribeSave();
  };
}

export function resetEditorUndoToastLifecycleForTests(): void {
  endEditorUndoToastScreenTransitionSession();
}
